import type { HTMLAttributes } from 'react';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import type { CarouselAPI } from '@elfsight/lib-carousel';
import {
  Carousel as CarouselComponent,
  CarouselItem as ItemWrapper
} from '@elfsight/lib-carousel';
import useMergedRef from '@react-hook/merged-ref';
import { useSettings } from '@wu/widget-core';
import { clamp } from 'es-toolkit';
import { useRect } from 'react-use-rect';
import type { Job } from '../../../types';
import { Item } from '../items';
import { LayoutContainer } from '../LayoutContainer';
import { useLayout } from '../LayoutProvider';

type CarouselProps = HTMLAttributes<HTMLDivElement> & {
  jobs: Job[];
};

export const Carousel = forwardRef<HTMLDivElement, CarouselProps>(
  ({ jobs, ...forwardingProps }, ref) => {
    const carouselRef = useRef<CarouselAPI>(null);
    const {
      carouselLayoutColumns: derivedColumns,
      carouselLayoutGap: gap,
      carouselLayoutShowArrows: showArrows,
      carouselLayoutAutorotation: autorotation,
      carouselLayoutAutorotationDelay: delay,
      displayDeactivation: isWidgetDeactivated
    } = useSettings();

    const { setRerender, setAutoplay } = useLayout();
    const [width, setWidth] = useState(0);
    const [measureRef] = useRect(({ width }) => setWidth(width), {
      resize: true
    });
    const mergedRef = useMergedRef(ref, measureRef);
    const dataLength = jobs.length;
    const columnBasis = 280;
    const columns = useMemo(() => {
      if (derivedColumns === 'auto') {
        const calcColumns = Math.floor(width / columnBasis);

        return clamp(calcColumns, 1, dataLength);
      }

      return derivedColumns;
    }, [width, dataLength, derivedColumns]);
    const isAutoplayEnabled = autorotation && !isWidgetDeactivated;

    useEffect(() => {
      if (!carouselRef.current) {
        return;
      }

      const { updateAutoHeight, autoplayStart, autoplayStop } =
        carouselRef.current;

      setRerender?.(updateAutoHeight);
      setAutoplay?.({
        enabled: isAutoplayEnabled,
        start: autoplayStart,
        stop: autoplayStop
      });
    }, [setRerender, setAutoplay, isAutoplayEnabled, derivedColumns]);

    return (
      <LayoutContainer gap={0} ref={mergedRef} {...forwardingProps}>
        <CarouselComponent
          ref={carouselRef}
          itemsPerSlide={columns}
          gap={gap}
          withArrowControl={showArrows}
          arrowControlMargin={8}
          withAutoplay={isAutoplayEnabled}
          autoplayDelay={delay}
          itemWidth={280}
          loop
        >
          {jobs.map((item) => (
            <ItemWrapper key={item.id}>
              <Item data={item} />
            </ItemWrapper>
          ))}
        </CarouselComponent>
      </LayoutContainer>
    );
  }
);
